import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  GpsApiResponse,
  GpsAssetMapping,
  GpsConnector,
  GpsConnectorTestResponse,
  GpsContainerLiveLocation,
  GpsLocation,
  GpsLocationHistoryPoint,
  GpsProvider,
  GpsProviderDevice,
  GpsTrackingSource,
  GpsVehicleEvent,
  GpsVehicleLive
} from './gps-tracking.model';

@Injectable({
  providedIn: 'root'
})
export class GpsTrackingService {
  private readonly gpsBaseUrl = `${environment.restApiUrl}gps-tracking`;
  private readonly containerBaseUrl = `${environment.restApiUrl}container-tracking/cases`;

  constructor(private readonly http: HttpClient) {}

  getProviders(): Observable<GpsProvider[]> {
    return this.http.get<GpsApiResponse<GpsProvider[]>>(`${this.gpsBaseUrl}/providers`)
      .pipe(map(response => response.data ?? []));
  }

  getConnectors(companyId: number): Observable<GpsConnector[]> {
    return this.http.get<GpsApiResponse<GpsConnector[]>>(`${this.gpsBaseUrl}/connectors`, {
      params: this.params({ companyId })
    }).pipe(map(response => response.data ?? []));
  }

  saveConnector(connector: GpsConnector): Observable<GpsConnector> {
    const request = connector.id
      ? this.http.put<GpsApiResponse<GpsConnector>>(`${this.gpsBaseUrl}/connectors/${connector.id}`, connector)
      : this.http.post<GpsApiResponse<GpsConnector>>(`${this.gpsBaseUrl}/connectors`, connector);
    return request.pipe(map(response => response.data));
  }

  testConnector(connectorId: number, companyId: number): Observable<GpsConnectorTestResponse> {
    return this.http.post<GpsApiResponse<GpsConnectorTestResponse>>(
      `${this.gpsBaseUrl}/connectors/${connectorId}/test`,
      null,
      { params: this.params({ companyId }) }
    ).pipe(map(response => response.data));
  }

  syncConnectorNow(connectorId: number, companyId: number): Observable<GpsConnector> {
    return this.http.post<GpsApiResponse<GpsConnector>>(
      `${this.gpsBaseUrl}/connectors/${connectorId}/sync-now`,
      null,
      { params: this.params({ companyId }) }
    ).pipe(map(response => response.data));
  }

  getMappings(connectorId: number, companyId: number): Observable<GpsAssetMapping[]> {
    return this.http.get<GpsApiResponse<GpsAssetMapping[]>>(`${this.gpsBaseUrl}/connectors/${connectorId}/mappings`, {
      params: this.params({ companyId })
    }).pipe(map(response => response.data ?? []));
  }

  getUnmappedDevices(connectorId: number, companyId: number): Observable<GpsProviderDevice[]> {
    return this.http.get<GpsApiResponse<GpsProviderDevice[]>>(`${this.gpsBaseUrl}/connectors/${connectorId}/unmapped-devices`, {
      params: this.params({ companyId })
    }).pipe(map(response => response.data ?? []));
  }

  saveMapping(connectorId: number, companyId: number, mapping: GpsAssetMapping): Observable<GpsAssetMapping> {
    return this.http.post<GpsApiResponse<GpsAssetMapping>>(
      `${this.gpsBaseUrl}/connectors/${connectorId}/mappings`,
      mapping,
      { params: this.params({ companyId }) }
    ).pipe(map(response => response.data));
  }

  deactivateMapping(connectorId: number, companyId: number, mappingId: number): Observable<boolean> {
    return this.http.delete<GpsApiResponse<boolean>>(
      `${this.gpsBaseUrl}/connectors/${connectorId}/mappings/${mappingId}`,
      { params: this.params({ companyId }) }
    ).pipe(map(response => !!response.data));
  }

  getTrackingSources(companyId: number): Observable<GpsTrackingSource[]> {
    return this.http.get<GpsApiResponse<GpsTrackingSource[]>>(
      `${this.gpsBaseUrl}/companies/${companyId}/tracking-sources`
    ).pipe(map(response => response.data ?? []));
  }

  getCompanyVehicleLive(companyId: number, sourceKey?: string): Observable<GpsVehicleLive[]> {
    return this.http.get<GpsApiResponse<GpsVehicleLive[]>>(`${this.gpsBaseUrl}/companies/${companyId}/vehicles/live`, {
      params: this.params({ sourceKey })
    }).pipe(map(response => response.data ?? []));
  }

  getCompanyVehicleLatest(companyId: number, sourceKey?: string): Observable<GpsLocation[]> {
    return this.http.get<GpsApiResponse<GpsLocation[]>>(`${this.gpsBaseUrl}/companies/${companyId}/vehicles/latest`, {
      params: this.params({ sourceKey })
    }).pipe(map(response => response.data ?? []));
  }

  getVehicleLive(companyId: number, vehicleId: number, sourceKey?: string): Observable<GpsVehicleLive> {
    return this.http.get<GpsApiResponse<GpsVehicleLive>>(`${this.gpsBaseUrl}/vehicles/${vehicleId}/live`, {
      params: this.params({ companyId, sourceKey })
    }).pipe(map(response => response.data));
  }

  getVehicleEvents(companyId: number, vehicleId: number, sourceKey?: string, from?: Date, to?: Date, limit = 100): Observable<GpsVehicleEvent[]> {
    return this.http.get<GpsApiResponse<GpsVehicleEvent[]>>(`${this.gpsBaseUrl}/vehicles/${vehicleId}/events`, {
      params: this.params({
        companyId,
        sourceKey,
        from: from?.toISOString(),
        to: to?.toISOString(),
        limit
      })
    }).pipe(map(response => response.data ?? []));
  }

  getVehicleHistory(companyId: number, vehicleId: number, sourceKey?: string, from?: Date, to?: Date): Observable<GpsLocationHistoryPoint[]> {
    return this.http.get<GpsApiResponse<GpsLocationHistoryPoint[]>>(`${this.gpsBaseUrl}/vehicles/${vehicleId}/history`, {
      params: this.params({
        companyId,
        sourceKey,
        from: from?.toISOString(),
        to: to?.toISOString()
      })
    }).pipe(map(response => response.data ?? []));
  }

  getContainerLiveLocation(companyId: number, caseId: number, sourceKey?: string): Observable<GpsContainerLiveLocation> {
    return this.http.get<GpsApiResponse<GpsContainerLiveLocation>>(`${this.containerBaseUrl}/${caseId}/live-location`, {
      params: this.params({ companyId, sourceKey })
    }).pipe(map(response => response.data));
  }

  private params(values: Record<string, string | number | boolean | undefined | null>): HttpParams {
    let params = new HttpParams();
    Object.entries(values).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return params;
  }
}
